import { useState, useCallback } from 'react';
import { Eye, EyeOff, Lock, Mail, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isFirstTimeLogin, setIsFirstTimeLogin] = useState(false);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const { login } = useAuth();

  const checkEmail = useCallback(async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setIsCheckingEmail(true);
    setError('');

    try {
      const checkResponse = await fetch('/api/auth/check-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const checkData = await checkResponse.json();
      
      if (checkResponse.ok && !checkData.hasPassword) {
        // User exists but needs to create password
        setUserInfo(checkData.user);
        setIsFirstTimeLogin(true);
        setError('');
      } else if (checkResponse.ok && checkData.hasPassword) {
        setError('You already have a password. Please enter it below to login.');
      } else {
        setError(checkData.error || 'Email not found in system');
      }
    } catch (checkError) {
      console.error('Error checking email:', checkError);
      setError('An error occurred while checking your email');
    } finally {
      setIsCheckingEmail(false);
    }
  }, [email]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (isFirstTimeLogin) {
        // First-time login: create password
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setIsLoading(false);
          return;
        }
        
        if (password.length < 6) {
          setError('Password must be at least 6 characters long');
          setIsLoading(false);
          return;
        }

        const response = await fetch('/api/auth/create-password', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ email, password }),
        });

        const data = await response.json();

        if (response.ok) {
          // Password created successfully, user is now logged in
          window.location.reload(); // Refresh to trigger session check
        } else {
          setError(data.error || 'Failed to create password');
        }
      } else {
        // Check if user needs first-time login BEFORE attempting regular login
        if (!password) {
          // If no password entered, check if user exists and needs first-time setup
          try {
            const checkResponse = await fetch('/api/auth/check-email', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ email }),
            });

            const checkData = await checkResponse.json();
            
            if (checkResponse.ok && !checkData.hasPassword) {
              // User exists but needs to create password
              setUserInfo(checkData.user);
              setIsFirstTimeLogin(true);
              setError('');
              setIsLoading(false);
              return;
            } else if (checkResponse.ok && checkData.hasPassword) {
              setError('Please enter your password to login.');
              setIsLoading(false);
              return;
            }
          } catch (checkError) {
            console.error('Error checking email:', checkError);
          }
        }

        // Regular login
        const result = await login(email, password);
        
        if (!result.success) {
          // Check if user needs first-time login
          if (result.error === 'NO_PASSWORD') {
            // User exists but needs to create password - switch to first-time mode
            try {
              const checkResponse = await fetch('/api/auth/check-email', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
              });

              const checkData = await checkResponse.json();
              
              if (checkResponse.ok && !checkData.hasPassword) {
                setUserInfo(checkData.user);
                setIsFirstTimeLogin(true);
                setError('');
                setIsLoading(false);
                return;
              }
            } catch (checkError) {
              console.error('Error checking email:', checkError);
            }
          }
          
          // Map error codes to user-friendly messages
          let errorMessage = 'Invalid email or password'; // default
          
          switch (result.error) {
            case 'EMAIL_NOT_FOUND':
              errorMessage = 'Email address not found in our system';
              break;
            case 'INVALID_PASSWORD':
              errorMessage = 'Incorrect password';
              break;
            case 'NO_PASSWORD':
              errorMessage = 'Please create your password to login';
              break;
            case 'NETWORK_ERROR':
              errorMessage = 'Network error. Please check your connection and try again';
              break;
            case 'LOGIN_FAILED':
              errorMessage = 'An error occurred during login. Please try again';
              break;
            default:
              errorMessage = 'Invalid email or password';
          }
          
          setError(errorMessage);
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  }, [email, password, confirmPassword, isFirstTimeLogin, login]);

  return (
    <div className="min-h-screen bg-[#1B2225] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-28 w-28 rounded-full overflow-hidden">
            <img src="/red-rocket.png?v=2" alt="Rocket Logo" className="w-full h-full object-cover" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-100">
            {isFirstTimeLogin ? 'Welcome to Hunterlodge GPT Launcher' : 'Sign in to Hunterlodge GPT Launcher'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-300">
            {isFirstTimeLogin ? `Hello ${userInfo?.name}! Create your password to get started` : 'Access your internal specialist GPTs'}
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-100">
                Email address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 pl-10 border border-gray-600 placeholder-gray-400 text-gray-100 bg-[#21282D] rounded-lg focus:outline-none focus:ring-red-500 focus:border-red-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your email"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-100">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 pl-10 pr-10 border border-gray-600 placeholder-gray-400 text-gray-100 bg-[#21282D] rounded-lg focus:outline-none focus:ring-red-500 focus:border-red-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
            
            {isFirstTimeLogin && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-100">
                  Confirm Password
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="appearance-none relative block w-full px-3 py-2 pl-10 pr-10 border border-gray-600 placeholder-gray-400 text-gray-100 bg-[#21282D] rounded-lg focus:outline-none focus:ring-red-500 focus:border-red-500 focus:z-10 sm:text-sm"
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {error && (
            <div className="bg-red-900/20 border border-red-600 text-red-300 px-4 py-3 rounded-lg">
              <div className="font-bold mb-1">Login Error:</div>
              <div>{error}</div>
            </div>
          )}

          {!isFirstTimeLogin && email && !password && (
            <div>
              <button
                type="button"
                onClick={checkEmail}
                disabled={isCheckingEmail}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
              >
                {isCheckingEmail ? 'Checking...' : 'First time here? Create a password →'}
              </button>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (isFirstTimeLogin ? 'Creating password...' : 'Signing in...') : (isFirstTimeLogin ? 'Create Password & Login' : 'Sign in')}
            </button>
          </div>

          {!isFirstTimeLogin && (
            <div className="text-center mt-4">
              <Link
                to="/forgot-password"
                className="text-sm text-blue-400 hover:text-blue-300 font-medium"
              >
                Forgot your password?
              </Link>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login; 
